import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardActionArea,
    CardMedia,
    CardContent,
    Checkbox,
    Collapse,
    IconButton,
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
//Standard Api
import axios from "axios";

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { createApiClient } from "../../components/useApiClient";
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";

import { ArtworkStub } from "../../models/Artwork";

export default function ArtworkPicker({ selected, onChange, expandView, artworkScope }: any) {
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    //Support API Calls
    const [isLoading, setIsLoading] = useState(false);
    const { activeClientId, getAccessToken, user } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();

    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );

    const [expanded, setExpanded] = useState(expandView);
    const [artworks, setArtworks] = useState<ArtworkStub[]>([]);
    //API Calls
    const artworksReq = useSafeRequest<ArtworkStub[]>(api, { maxAttempts: 3 });

    const loadAll = async () => {

        setIsLoading(true);

        try {
            const tasks = [
                artworksReq.run({ method: "GET", url: `artworks` }),
            ];

            const [a] = await Promise.allSettled(tasks);

            if (a.status === "fulfilled") setArtworks(a.value);
        }
        catch (error) {
            console.error('Failed to load artworks data:', error);
        } finally {
            //Loading complete
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, [activeClientId]);

    useEffect(() => {
        artworkScope?.(artworks);
    }, [artworks]);

    const toggle = (id: number) => {
        onChange(
            selected.includes(id)
                ? selected.filter((x: number) => x !== id)
                : [...selected, id]
        );
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Associated Artworks
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        aria-label={expanded ? 'Collapse filters' : 'Expand filters'}
                    >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>
            </Box>
        <Collapse in={expanded}>
        <Box sx={{ maxHeight: 420, overflowY: "auto", border: "1px solid #ddd", borderRadius: 2, p: 1 }}>

            <Grid container spacing={1}>
                {artworks.map((a) => {
                    const selectedState = selected.includes(a.artworkId);
                    const imageUrl =
                        a.imageUrl
                            ? a.imageUrl
                            : "/images/logo.png";

                    return (
                        <Grid size={{ xs: 6, sm: 4, md: 3, lg: 2 }} key={a.artworkId}>
                            <Card
                                sx={{
                                    border: selectedState ? "2px solid #1976d2" : "1px solid #ccc",
                                    borderRadius: 2,
                                    boxShadow: selectedState ? "0 0 6px rgba(25,118,210,0.4)" : "none",
                                    position: "relative",
                                    transition: "0.2s ease",
                                }}
                            >
                                <CardActionArea onClick={() => toggle(a.artworkId)}>
                                    <Checkbox
                                        checked={selectedState}
                                        onChange={() => toggle(a.artworkId)}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: 4,
                                            right: 4,
                                            background: "rgba(255,255,255,0.8)",
                                            borderRadius: "4px",
                                        }}
                                    />
                                    <CardMedia
                                        component="img"
                                        height="100"
                                        image={imageUrl}
                                        alt={a.artWorkTitle}
                                        sx={{
                                            objectFit: "cover",
                                            borderBottom: "1px solid #eee",
                                        }}
                                    />
                                    <CardContent
                                        sx={{
                                            p: 1,
                                            "&:last-child": { pb: 1 },
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={selectedState ? 600 : 400}
                                            color="text.primary"
                                            sx={{
                                                display: "-webkit-box",
                                                WebkitLineClamp: 2, // limit to two lines
                                                WebkitBoxOrient: "vertical",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                minHeight: "2.6em",
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {a.artWorkTitle}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                mt: 0.25,
                                            }}
                                        >
                                            {a.artistName}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {artworks.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: "center" }}>
                    No artworks found.
                </Typography>
            )}
            </Box>
            </Collapse>
        </>
    );
}
